import {
    Box, Button, Divider, HStack, ListItem, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, OrderedList, Popover, PopoverBody, PopoverContent, PopoverTrigger, Skeleton, SkeletonText, Text, Tooltip, useDisclosure,
    useToast, VStack
} from "@chakra-ui/react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Form, Formik } from "formik";
import React, { useContext, useState } from "react";
import useFetch, { CachePolicies } from "use-http";
import * as Yup from "yup";
import { currencySymbol } from "../../../utils";
import type { CreditBalanceApiResponse } from '../../interfaces';
import { UserContext } from '../../providers';
import { TextField } from "../Form";

// Replace with your Stripe public key

export const CreditBalanceDetails = ({ data, refreshBalance }: { data: CreditBalanceApiResponse, refreshBalance: Function }) => {
    const { user } = useContext(UserContext);

    if (!user) return null;

    const stripePromise = loadStripe(user.stripePublishKey);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const toast = useToast();

    const USD_MIN = 500;
    const USD_MAX = 5000;
    const USD_TO_INR = 85;

    const currencyConfig = {
        USD: { min: USD_MIN, max: USD_MAX, discountMultiplier: 0.85 },
        INR: { min: USD_MIN * USD_TO_INR, max: USD_MAX * USD_TO_INR, discountMultiplier: 0.85 },
    };

    if (!data.currency) return null;

    const { min, max, discountMultiplier } = currencyConfig[data.currency];

    const { post, response, loading } = useFetch({
        cachePolicy: CachePolicies.NO_CACHE,
    });

    const handleContinue = async (amount: number) => {
        const result = await post(`/api/billing/credit-balance/${user.clientNumber}`, {
            amount,
            currency: data.currency.toUpperCase(),
        });

        if (response.ok) {
            setClientSecret(result.client_secret);
        } else {
            toast({
                title: "Error",
                description: result?.message || "Failed to initiate payment",
                status: "error",
                duration: 5000,
            });
        }
    };

    const handleModalClose = (refresh = false) => {
        setClientSecret(null); // Reset clientSecret
        onClose(); // Close the modal
        if (refresh) {
            refreshBalance();
        }
    };

    return (
        <VStack align="left">
            <HStack>
                <Text fontWeight="bold" color="appSlate">
                    Credit balance
                </Text>
                <Popover trigger="hover">
                    <PopoverTrigger>
                        <img src="https://ik.imagekit.io/ikmedia/info.svg" alt="Info" />
                    </PopoverTrigger>
                    <PopoverContent>
                        <PopoverBody>
                            Your credit balance will automatically be applied to reduce the amount on future invoices.
                        </PopoverBody>
                    </PopoverContent>
                </Popover>
            </HStack>
            <Text fontSize="xl">
                {currencySymbol(data.currency)}{data.balance * -1}
            </Text>

            <Button w="fit-content" disabled={!data.canAddCredit} onClick={onOpen} variant="outline">
                <Tooltip
                    label={
                        !data.canAddCredit
                            ? "You can add credit only if you have an active subscription and are not on a custom pricing plan"
                            : ""
                    }
                    isDisabled={data.canAddCredit}
                >
                    Add to credit balance
                </Tooltip>
            </Button>


            <Box pb={3} pt={3}>
                <Divider />
            </Box>


            <Text fontWeight="bold" color="appSlate">
                About credit balance
            </Text>

            <OrderedList pl={6}>
                <ListItem>
                    Your credit balance will automatically be applied to reduce the amount on your future invoices.
                </ListItem>
                <ListItem>
                    Adding credit is available only for accounts with an active subscription and without a custom pricing plan. If you face issues with monthly auto-debit due to RBI guidelines or similar restrictions, adding credit upfront can help avoid interruptions.
                </ListItem>
                <ListItem>
                    You get a 15% discount when you pay upfront. For example, adding $1000 to your credit balance will cost you $850.
                </ListItem>
                <ListItem>
                    Your credit balance will typically be updated as soon as the payment is processed. If authentication (e.g., 3D Secure) is required, you will be redirected to your bank's authentication page. Check your email for payment confirmation or any pending actions. Once the payment is confirmed, a receipt will be sent to the billing contact email. In some cases, it may take up to an hour to reflect. If it doesn't update after this, please contact support.
                </ListItem>
            </OrderedList>

            <Modal isOpen={isOpen} onClose={handleModalClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Add to credit balance</ModalHeader>
                    <ModalCloseButton />
                    {!clientSecret ? (
                        <Formik
                            initialValues={{ amount: min }}
                            validationSchema={Yup.object().shape({
                                amount: Yup.number()
                                    .typeError("Enter a whole number (numbers only, no cents)")
                                    .integer("No cents allowed")
                                    .transform((value, originalValue) =>
                                        originalValue ? Number(originalValue) : value
                                    )
                                    .min(min, `The minimum amount is ${currencySymbol(data.currency)}${min}`)
                                    .max(max, `The maximum amount is ${currencySymbol(data.currency)}${max}`)
                                    .required("Amount is required"),
                            })}
                            onSubmit={(values) => handleContinue(Number(values.amount))}
                        >
                            {({ values, errors, isSubmitting }) => (
                                <Form>
                                    <ModalBody pb={4}>
                                        <TextField
                                            name="amount"
                                            label={`Enter amount (Between ${currencySymbol(data.currency)}${min} and ${currencySymbol(data.currency)}${max})`}
                                            required={true}
                                        />
                                        {!errors.amount && (
                                            <Box mt={4} p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                                                <Text fontSize="lg" fontWeight="bold">
                                                    You pay: {currencySymbol(data.currency)}
                                                    {Math.ceil(values.amount * discountMultiplier)}
                                                </Text>
                                                <Text fontSize="sm" color="green">
                                                    Save {currencySymbol(data.currency)}{values.amount - Math.ceil(values.amount * discountMultiplier)} with a 15% discount for upfront payment
                                                </Text>
                                            </Box>
                                        )}
                                    </ModalBody>
                                    <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                                        <Button onClick={() => {
                                            handleModalClose();
                                        }} variant="outline">
                                            Cancel
                                        </Button>
                                        <Button
                                            ml={3}
                                            type="submit"
                                            isLoading={isSubmitting || loading}
                                            isDisabled={
                                                isSubmitting || loading || !values.amount || values.amount < min || values.amount > max
                                            }
                                        >
                                            Continue
                                        </Button>
                                    </ModalFooter>
                                </Form>
                            )}
                        </Formik>
                    ) : (
                        <Elements stripe={stripePromise} options={{ clientSecret }}>
                            <StripePaymentForm onClose={handleModalClose} />
                        </Elements>
                    )}
                </ModalContent>
            </Modal>
        </VStack>
    );
};

const StripePaymentForm = ({ onClose }: { onClose: (refresh?: boolean) => void }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [message, setMessage] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [formReady, setFormReady] = useState(false);
    const toast = useToast();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setIsLoading(true);

        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
        });

        if (error) {
            setMessage(error.message || "An unexpected error occurred.");
        }

        if (paymentIntent?.status === 'succeeded') {
            // Wait for the webhook to update the credit balance
            await new Promise(resolve => setTimeout(resolve, 3000));
            toast({
                title: "Success",
                description: "Payment successful. If your credit balance is not updated in one hour, please contact support.",
                status: "success",
                duration: 5000,
            });
        } else if (!error && paymentIntent) {
            toast({
                title: "Success",
                description: "Your credit balance will be updated as soon as the payment is confirmed.",
                status: "success",
                duration: 5000,
            });
        }
        setIsLoading(false);
        onClose(true);
    };

    return (
        <form onSubmit={handleSubmit}>
            <ModalBody pb={4} minH={"300px"}>
                {!formReady && <StripePaymentFormSkeleton />}
                <Box display={formReady ? 'block' : 'none'}>
                    <PaymentElement
                        onReady={() => {
                            setFormReady(true);
                        }}
                    />
                </Box>
                {message && <Text color="red.500" mt={4}>{message}</Text>}
            </ModalBody>
            <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button onClick={() => {
                    onClose();
                }} variant="outline">
                    Cancel
                </Button>
                <Button
                    type="submit"
                    isLoading={isLoading}
                    isDisabled={!stripe || !elements}
                >
                    Pay Now
                </Button>
            </ModalFooter>
        </form>
    );
};

const StripePaymentFormSkeleton = () => {
    return <VStack spacing={4} align="stretch">
        {/* Skeleton for tabs (Card, Bank) */}
        <HStack>
            <Skeleton height="40px" width="50%" />
            <Skeleton height="40px" width="50%" />
        </HStack>

        {/* Skeleton for Card number field */}
        <SkeletonText mt={4} noOfLines={1} spacing="4" skeletonHeight="8" />
        <Skeleton height="40px" width="100%" />

        {/* Skeleton for Expiry Date and Security Code */}
        <HStack spacing={4} mt={4}>
            <Skeleton height="40px" width="50%" />
            <Skeleton height="40px" width="50%" />
        </HStack>

        {/* Skeleton for Country dropdown */}
        <SkeletonText mt={4} noOfLines={1} spacing="4" skeletonHeight="8" />
        <Skeleton height="40px" width="100%" />
    </VStack>
}